import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  FixturesShowcase,
  SectionCard,
  StatusPill,
} from "components/admin";
import { SchoolMatchWeekHighlights } from "components/admin/school/school-match-week-highlights";
import { workspaceApi } from "pages/app/workspace/services";
import { SchoolTeamProfileTabProps } from "pages/app/workspace/school-team-profile/school-team-profile.types";
import { HeroSurfacePanel } from "pages/app/workspace/sections/styles/workspace-section.styles";
import {
  SchoolTeamProfileActionLink,
  SchoolTeamProfileBadgeRow,
  SchoolTeamProfileContentStack,
  SchoolTeamProfileEyebrowText,
  SchoolTeamProfileHeroEyebrow,
  SchoolTeamProfileHeroImage,
  SchoolTeamProfileHeroMeta,
  SchoolTeamProfileHeroRow,
  SchoolTeamProfileHeroTextGroup,
  SchoolTeamProfileHeroTitle,
  SchoolTeamProfileMetadataGrid,
  SchoolTeamProfileMetadataTile,
  SchoolTeamProfileOverviewStack,
  SchoolTeamProfileSectionTertiary,
  SchoolTeamProfileTitleText,
} from "pages/app/workspace/styles/school-team-profile.page.inline.styles";
import { formatPortalLabel } from "utils/portal-access";

export const SchoolTeamOverviewTab: React.FC<SchoolTeamProfileTabProps> = ({
  profileBackTo,
  schoolId,
  team,
  teamId,
}) => {
  const history = useHistory();
  const dashboardQuery = useQuery({
    queryKey: ["school-team-profile-dashboard", teamId],
    queryFn: () => workspaceApi.schoolTeamDashboards.get(teamId),
    enabled: Boolean(teamId),
  });

  const upcomingMatches = useMemo(
    () => dashboardQuery.data?.match_week?.schedule || dashboardQuery.data?.upcoming_matches || [],
    [dashboardQuery.data?.match_week?.schedule, dashboardQuery.data?.upcoming_matches],
  );
  const playerCount =
    dashboardQuery.data?.overview?.player_count ?? team?.player_count ?? 0;
  const coachName = team?.coach?.name || team?.coach_name || "Unassigned";
  const liveLinks = dashboardQuery.data?.match_week?.live_links || {};
  const liveActions = [
    liveLinks.stream_url
      ? {
          href: liveLinks.stream_url,
          label: liveLinks.stream_label || "Watch live",
        }
      : null,
    liveLinks.score_url
      ? {
          href: liveLinks.score_url,
          label: "Live score",
        }
      : null,
    liveLinks.meeting_url
      ? {
          href: liveLinks.meeting_url,
          label: "Open link",
        }
      : null,
  ].filter(Boolean) as Array<{
    href: string;
    label: string;
  }>;

  return (
    <SchoolTeamProfileOverviewStack>
      <SectionCard
        subtitle="School team identity, programme context, and squad footprint."
        title="Team profile"
      >
        <SchoolTeamProfileContentStack>
          <HeroSurfacePanel>
            <SchoolTeamProfileHeroRow>
              <SchoolTeamProfileHeroImage
                alt={team.name || "Team"}
                fallbackLabel={team.name}
                src={team.image_url || team.image}
                variant="team"
              />
              <SchoolTeamProfileHeroTextGroup>
                <SchoolTeamProfileHeroEyebrow>
                  {team.sport_name || "School team"}
                </SchoolTeamProfileHeroEyebrow>
                <SchoolTeamProfileHeroTitle>{team.name || "Unknown team"}</SchoolTeamProfileHeroTitle>
                <SchoolTeamProfileHeroMeta>
                  {[team.school_name || "School pending", `Coach: ${coachName}`]
                    .filter(Boolean)
                    .join(" · ")}
                </SchoolTeamProfileHeroMeta>
                <SchoolTeamProfileBadgeRow>
                  <StatusPill value={team.active ? "active" : "inactive"} />
                  <span className="inline-flex items-center rounded-full border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] px-3 py-1 text-xs font-medium text-[var(--brand-muted)]">
                    {playerCount} {playerCount === 1 ? "player" : "players"}
                  </span>
                </SchoolTeamProfileBadgeRow>
              </SchoolTeamProfileHeroTextGroup>
            </SchoolTeamProfileHeroRow>
          </HeroSurfacePanel>

          <SchoolTeamProfileMetadataGrid>
            {[
              ["Age group", team.age_group || "Not set"],
              ["Team label", team.team_label || "Not set"],
              ["Season", team.season || "Current"],
              ["Competition", formatPortalLabel(team.competition_level || "not set")],
              ["Category", formatPortalLabel(team.gender_category || "mixed")],
            ].map(([label, value]) => (
              <SchoolTeamProfileMetadataTile key={String(label)}>
                <SchoolTeamProfileEyebrowText>{label}</SchoolTeamProfileEyebrowText>
                <SchoolTeamProfileTitleText>{value}</SchoolTeamProfileTitleText>
              </SchoolTeamProfileMetadataTile>
            ))}
          </SchoolTeamProfileMetadataGrid>
        </SchoolTeamProfileContentStack>
      </SectionCard>

      <SchoolMatchWeekHighlights
        isLoading={dashboardQuery.isLoading}
        matchWeek={dashboardQuery.data?.match_week}
        onSelectPlayer={
          schoolId
            ? (playerId) => {
                history.push({
                  pathname: `/workspace/school-players/${schoolId}/${playerId}`,
                  state: { backTo: profileBackTo },
                });
              }
            : undefined
        }
        showTeamOfTheWeek={false}
        subtitle="Upcoming fixtures, weekly highlights, and live links for this team."
        weeklyHighlights={dashboardQuery.data?.weekly_highlights}
      >
        {liveActions.length > 0 ? (
          <SchoolTeamProfileSectionTertiary>
            {liveActions.map((action) => (
              <SchoolTeamProfileActionLink
                href={action.href}
                key={action.label}
                rel="noreferrer"
                target="_blank"
              >
                {action.label}
              </SchoolTeamProfileActionLink>
            ))}
          </SchoolTeamProfileSectionTertiary>
        ) : null}

        <FixturesShowcase
          emptyDescription="Upcoming fixtures will appear here once this team has confirmed matchweek activity."
          emptyTitle="No team fixtures"
          fixtures={upcomingMatches}
          isLoading={dashboardQuery.isLoading}
        />
      </SchoolMatchWeekHighlights>
    </SchoolTeamProfileOverviewStack>
  );
};
